import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { Button } from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Breadcrumb from '../../../components/Breadcrumb';
import { PRODUCTMANAGE_PAGE } from '../../../routes/routeConstant';
import MultipleImageInput from '../../../components/InputImage';
import InputEn from '../../../components/InputEn';
import useTourPackage from '../../../stores/useTourPackage';
import { IItinerary, ITourPackage } from '../../../types/types';

export default function FormTour() {
  const backLink = `${PRODUCTMANAGE_PAGE}?page=Tour Package`;
  const breadcrumbPath = [{ name: 'Tour Package', link: backLink }, { name: 'Form Tour' }];
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchTours, tours, editTour, addTour, saveLoading } = useTourPackage();
  const editId = searchParams.get('edit');
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [currentTitles, setCurrentTitles] = useState<string[]>([]);
  const [itineraries, setItineraries] = useState<IItinerary[]>([]);

  const { register, handleSubmit, setValue, watch, reset } = useForm<ITourPackage>({
    defaultValues: {
      destination_tour: '',
      duration_tour: '',
      price_tour: undefined,
      inclusion_tour_id: '',
      exclusion_tour_id: '',
      inclusion_tour_en: '',
      exclusion_tour_en: '',
      image_tour: [],
      image_titles: [],
      itineraries: [],
    },
  });

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  useEffect(() => {
    if (editId) {
      const detailTour = tours?.find((tour) => tour.tour_id === Number(editId));
      if (detailTour) {
        setValue('destination_tour', detailTour.destination_tour);
        setValue('destination_tour_en', detailTour.destination_tour_en);
        setValue('duration_tour', detailTour.duration_tour);
        setValue('price_tour', Number(detailTour.price_tour));
        setValue('inclusion_tour_id', detailTour.inclusion_tour_id);
        setValue('exclusion_tour_id', detailTour.exclusion_tour_id);
        setValue('inclusion_tour_en', detailTour.inclusion_tour_en);
        setValue('exclusion_tour_en', detailTour.exclusion_tour_en);
        const images = detailTour.image_tour || []; // Pastikan array
        const titles = detailTour.image_titles || Array(images.length).fill(''); // Pastikan array
        setValue('image_tour', images);
        setValue('image_titles', titles);
        setCurrentImages(images);
        setCurrentTitles(titles);

        if (Array.isArray(detailTour.itineraries)) {
          const formattedItineraries = detailTour.itineraries.map((itn, index) => ({
            title_itn: index + 1,
            activity_itn_id: itn.activity_itn_id || '',
            activity_itn_en: itn.activity_itn_en || '',
          }));
          setValue('itineraries', formattedItineraries);
          setItineraries(formattedItineraries);
        }
      }
    }
  }, [editId, tours, setValue]);

  const onSubmit = async (data: ITourPackage) => {
    const modifiedData = {
      ...data,
      price_tour: Number(data.price_tour),
      itineraries: data.itineraries.map((itn) => ({
        ...itn,
        title_itn: Number(itn.title_itn),
      })),
    };
    console.log(modifiedData);
    const detailTour = tours?.find((tour) => tour.tour_id === Number(editId));
    if (editId && detailTour !== undefined) {
      await editTour(Number(editId), modifiedData);
    } else {
      await addTour(modifiedData);
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', 'list');
      return newParams;
    });
    console.log(modifiedData);
    reset();
    setCurrentImages([]);
    setCurrentTitles([]);
    setItineraries([]);
  };

  const handleImagesChange = ({ imageUrls, imageTitles }: { imageUrls: string[]; imageTitles: string[] }) => {
    setCurrentImages(imageUrls);
    setCurrentTitles(imageTitles);
    setValue('image_tour', imageUrls);
    setValue('image_titles', imageTitles);
  };

  const addItinerary = () => {
    setItineraries((prev) => [...prev, { title_itn: prev.length + 1, activity_itn_id: '', activity_itn_en: '' }]);
  };

  const removeItinerary = (index: number) => {
    setItineraries((prev) => prev.filter((_, i) => i !== index).map((itn, i) => ({ ...itn, title_itn: i + 1 })));
  };
  return (
    <div className="flex gap-4 flex-col">
      <Breadcrumb paths={breadcrumbPath} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col text-sm gap-2">
            <p>
              Destination <span className="text-red-600">*</span>
            </p>
            <InputEn
              type="text"
              nama="destination_tour"
              namaEn="destination_tour_en"
              placeholder="Destinasi tour..."
              register={register}
              watch={watch}
              setValue={setValue}
            />
          </div>

          <div className="flex gap-2 w-full">
            <div className="flex flex-col text-sm gap-2 w-full">
              <p>
                Duration <span className="text-red-600">*</span>
              </p>
              <Input {...register('duration_tour')} type="text" required placeholder="Durasi tour.. Cth. 3N/2D" />
            </div>

            <div className="flex flex-col text-sm gap-2 w-full">
              <p>
                Price <span className="text-red-600">*</span>
              </p>
              <Input {...register('price_tour')} type="number" required placeholder="Harga mulai dari..." />
            </div>
          </div>

          <div className="md:flex-row flex flex-col gap-3 w-full">
            <div className="flex flex-col text-sm gap-2 w-full">
              <p className="flex gap-1">
                Inclusion <span className="text-red-600">*</span>
              </p>
              <InputEn
                type="texteditor"
                nama="inclusion_tour_id"
                namaEn="inclusion_tour_en"
                placeholder="Masukkan inclusion paket..."
                register={register}
                watch={watch}
                setValue={setValue}
              />
            </div>
            <div className="flex flex-col text-sm gap-2 w-full">
              <p className="flex gap-1">
                Exclusion <span className="text-red-600">*</span>
              </p>
              <InputEn
                type="texteditor"
                nama="exclusion_tour_id"
                namaEn="exclusion_tour_en"
                placeholder="Masukkan exclusion paket..."
                register={register}
                watch={watch}
                setValue={setValue}
              />
            </div>
          </div>

          <div className="flex gap-3 flex-col">
            {itineraries.map((itinerary, index) => (
              <div key={itinerary.title_itn} className="flex gap-3 w-full">
                <div className="flex flex-col text-sm gap-2 w-16">
                  <p className="flex gap-1">
                    Day<span className="text-red-600">*</span>
                  </p>
                  <Input
                    {...register(`itineraries.${index}.title_itn`)}
                    type="number"
                    value={itinerary.title_itn}
                    readOnly
                  />
                </div>
                <div className="flex flex-col text-sm gap-2 w-full">
                  <p className="flex gap-1">
                    Activity <span className="text-red-600">*</span>
                  </p>
                  <InputEn
                    type="texteditor"
                    nama={`itineraries.${index}.activity_itn_id`}
                    namaEn={`itineraries.${index}.activity_itn_en`}
                    placeholder="Masukkan aktivitas..."
                    register={register}
                    watch={watch}
                    setValue={setValue}
                  />
                </div>
                <div className="flex flex-col text-sm gap-2">
                  <div className="h-5" />
                  <Button type="button" variant="destructive" onClick={() => removeItinerary(index)}>
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
            <Button type="button" className="flex gap-1" variant="outline" onClick={addItinerary}>
              <Plus /> Tambah Itinerary
            </Button>
          </div>

          <MultipleImageInput
            onImagesChange={handleImagesChange}
            existingImages={currentImages || []} // Tambahan pengecekan
            existingTitles={currentTitles || []} // Tambahan pengecekan
          />

          <div className="flex gap-2">
            <Button variant="secondary" type="button">
              <Link to={backLink}>Batal</Link>
            </Button>
            <Button type="submit" disabled={saveLoading}>
              {saveLoading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
